/**
 * Parsing timestamps the API sends.
 *
 * Every datetime column in this system stores **naive UTC** — the backend
 * writes `datetime.utcnow()` and serialises with `.isoformat()`, producing
 * `"2026-08-15T14:14:33.437657"` with no offset.
 *
 * `new Date()` reads a date-time string WITHOUT an offset as LOCAL time. So the
 * browser took a UTC value and labelled it local: every timestamp in the
 * product displayed 5½ hours early for a reader in IST, 2 hours early in CEST,
 * and correctly only in UTC. Nothing looked broken — the times were plausible,
 * consistently wrong, and wrong by an amount that varies with who is reading.
 *
 * The fix belongs here rather than at 38 render sites: treat an offset-less
 * string as the UTC it actually is, and let `toLocaleString` put it back into
 * the reader's timezone. `tests/datetime.test.ts` covers the cases, and
 * `tests/no-raw-date-parse.test.ts` stops a new screen reintroducing it.
 *
 * The real fix is timezone-aware columns end to end, so the API states the zone
 * instead of leaving the client to assume it. Recorded in the backlog; this
 * makes the display correct today without a migration touching every table.
 */

/** Does this string already state its timezone? */
function hasTimezone(value: string): boolean {
  // A trailing Z, or ±HH:MM / ±HHMM after the time portion.
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());
}

/**
 * Parse a timestamp from the API into a Date.
 *
 * Offset-less strings are read as UTC, which is what they are. Strings that
 * already carry an offset are left alone, so this stays correct if the backend
 * later starts emitting them — the two can coexist during that change.
 *
 * Returns an Invalid Date for null/undefined/unparseable input rather than
 * throwing: a missing timestamp should render as a dash, not take down a table.
 */
export function parseApiDate(value: string | number | Date | null | undefined): Date {
  if (value instanceof Date) return value;
  if (value === null || value === undefined || value === "") return new Date(NaN);
  if (typeof value === "number") return new Date(value);

  const raw = String(value).trim();
  // A date with no time component is a plain calendar date; adding a zone would
  // shift it across midnight for readers behind UTC.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  if (dateOnly || hasTimezone(raw)) return new Date(raw);

  return new Date(`${raw}Z`);
}

/** True when a parsed timestamp is usable — cheaper to read than isNaN chains. */
export function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

/** What every screen shows in place of a timestamp it does not have. */
export const NO_DATE = "—";

/**
 * Note on `timeZoneName`.
 *
 * `dateStyle` / `timeStyle` cannot be combined with individual component
 * options — Intl throws a TypeError — and `timeZoneName` is one of those. So
 * these spell the components out. It is more verbose and it is the only way to
 * name the zone.
 */
const DATE_TIME: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const DATE_TIME_SECONDS: Intl.DateTimeFormatOptions = { ...DATE_TIME, second: "2-digit" };

const DATE_ONLY: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
};

const TIME_ONLY: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

function render(
  value: Parameters<typeof parseApiDate>[0],
  options: Intl.DateTimeFormatOptions
): string {
  const date = parseApiDate(value);
  // A missing or unparseable timestamp renders as a dash. It used to render as
  // the literal string "Invalid Date", which looks like a bug in the data
  // rather than an absence in it.
  if (!isValidDate(date)) return NO_DATE;
  return date.toLocaleString(undefined, options);
}

/**
 * Date and time, in the reader's timezone, WITH the zone named.
 *
 * The zone is the point. Rendering in local time is correct but silent, and
 * these timestamps are evidence: when a claimant signed, when a notice went
 * out, when a case was reclassified. Three people in three countries reading
 * the same admin screen were seeing three different numbers with nothing on
 * screen to say so.
 *
 * `seconds` for forensic ordering — the Activity Log, where two events a second
 * apart need to be distinguishable.
 */
export function formatDateTime(
  value: Parameters<typeof parseApiDate>[0],
  opts: { seconds?: boolean } = {}
): string {
  return render(value, opts.seconds ? DATE_TIME_SECONDS : DATE_TIME);
}

/** Calendar date only. No zone, because a date without a time does not have one. */
export function formatDate(value: Parameters<typeof parseApiDate>[0]): string {
  return render(value, DATE_ONLY);
}

/** Clock time only, with the zone named for the same reason as above. */
export function formatTime(value: Parameters<typeof parseApiDate>[0]): string {
  return render(value, TIME_ONLY);
}
